import sabApi from '@/api/sab'
import peanutbutterApi from '@/api/peanutbutter'

const state = {
  accountId: null,
  credentialKey: null,
  isProfileLoaded: false,
  isSubscribed: false,
  subscription: {},
  signedInPlatform: null
}

function getCredential(credentials) {
  if (!Array.isArray(credentials) || !credentials.length) {
    return null
  }

  const row = credentials.find(it => it?.kind === 'email_password')
  return row && typeof row.email === 'string' ? row.email : null
}

function hasBasicProfileIdentity(userData) {
  if (userData.user_id != null || userData.id != null || userData.email != null) {
    return true
  }
  if (userData.account_id != null) {
    return true
  }

  const phoneLike =
    userData.phone ?? userData.phone_number ?? userData.msisdn ?? userData.mobile ?? userData.mobile_phone
  if (phoneLike != null && String(phoneLike).trim() !== '') {
    return true
  }

  const credentials = userData.credentials
  if (Array.isArray(credentials)) {
    for (const c of credentials) {
      if (!c || typeof c !== 'object') {
        continue
      }
      const kind = typeof c.kind === 'string' ? c.kind.toLowerCase() : ''
      if (
        kind.includes('phone') &&
        (c.phone_number != null || c.phone != null || c.country_code != null || c.msisdn != null)
      ) {
        return true
      }
    }
  }

  return false
}

const getters = {}

const actions = {
  /** Peanut/device unauthenticated install — required before auth refresh on cold load. */
  async install() {
    try {
      const res = await peanutbutterApi.install()
      if (!res.ok) {
        throw new Error(`install ${res.status}`)
      }
    } catch {
      // Non-fatal: SAB flows may still work; avoids blocking app bootstrap.
    }
  },
  async verifyProfile({ commit }) {
    const [accountResult, subscriptionResult, userResult] = await Promise.allSettled([
      sabApi.verifyProfile(),
      sabApi.subscriptionsStatus(),
      sabApi.getUser()
    ])

    const account = accountResult.status === 'fulfilled' ? accountResult.value : {}
    const subscription = subscriptionResult.status === 'fulfilled' ? subscriptionResult.value : {}
    const user = userResult.status === 'fulfilled' ? userResult.value : {}
    const userData = {
      ...account,
      ...subscription,
      ...user,
      account,
      subscription,
      user
    }

    if (hasBasicProfileIdentity(userData)) {
      commit('SET_USER_PROFILE', userData)
      commit('SET_PROFILE_LOADED', true)
    } else {
      const anyFulfilled =
        accountResult.status === 'fulfilled' ||
        userResult.status === 'fulfilled' ||
        subscriptionResult.status === 'fulfilled'
      if (anyFulfilled) {
        commit('SET_USER_PROFILE', userData)
        commit('SET_PROFILE_LOADED', true)
      } else {
        // Every SAB call rejected — still mark profile pass "done" so router guards do not hang
        // waiting on `isProfileLoaded` (e.g. offline / gateway errors after a valid token login).
        commit('SET_PROFILE_LOADED', true)
      }
    }
  },
  unset({ commit }) {
    commit('SET_USER_PROFILE', null)
    commit('SET_PROFILE_LOADED', false)
  }
}

const mutations = {
  SET_USER_PROFILE(state, userData) {
    if (!userData) {
      state.isSubscribed = false
      state.signedInPlatform = null
      state.accountId = null
      state.credentialKey = null
      state.subscription = {}
      return
    }

    state.accountId = userData.user_id
    state.signedInPlatform = userData.platform
    state.credentialKey = getCredential(userData.credentials)

    state.isSubscribed = typeof userData.has_access === 'boolean' ? userData.has_access : true
    state.subscription = userData.subscription
    state.userData = userData
  },
  SET_PROFILE_LOADED(state, isProfileLoaded) {
    state.isProfileLoaded = isProfileLoaded
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
